import { Box, Flex, Heading, Link, Paragraph } from "@eworkit/iris";
import { Formik, FormikHelpers, FormikProps } from "formik";
import React, { useContext, useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { Trans, useTranslation } from "react-i18next";

import { AnimatedLayoutModal } from "@/components/animated-layout";
import { applyApiErrors } from "@/components/Formik";
import { passwordReset } from "@/data/session";
import { MessagesContext } from "@/eworkitComponents";
import { Img } from "@/eworkitComponents";
import { useRecaptcha } from "@/infrastructure/Recaptcha";
import { ACTION_NAMES } from "@/infrastructure/Recaptcha/types";

import {
  FORM_EMPTY_INITIAL_VALUES,
  FORM_MESSENGER_CONTEXT,
  IPasswordResetFormValues,
  PasswordResetForm,
  validationSchema,
} from "./components";
import ResetPasswordImg from "./reset-password.svg";

interface IPasswordResetProps {}

export const SessionPasswordReset: React.FunctionComponent<IPasswordResetProps> = React.memo(() => {
  const [sent, setSent] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { addMessage } = useContext(MessagesContext);
  const { t } = useTranslation(["public", "common"]);

  const handleSubmit = async (values: IPasswordResetFormValues, actions: FormikHelpers<IPasswordResetFormValues>) => {
    try {
      const recaptchaToken = useRecaptcha && executeRecaptcha ? await executeRecaptcha(ACTION_NAMES.resetPassword) : "";
      await passwordReset({ recaptchaToken }, values);
      setSent(true);
    } catch (response) {
      addMessage({
        text: t("common:VALIDATION_MESSAGES.GENERAL"),
        type: "warning",
        context: FORM_MESSENGER_CONTEXT,
      });
      applyApiErrors(actions, response.payload);
      actions.setSubmitting(false);
    }
  };

  const renderSent = () => {
    return (
      <AnimatedLayoutModal size="md">
        <Box px={["sm", null, "xxl"]}>
          <Flex flexDirection="column" gap="md">
            <Heading size="h-md" align="center">
              {t("SESSION_SIGNUP.USER_FORM.RESET_PASSWORD")}
            </Heading>
            <Flex justifyContent="center">
              <Img src={ResetPasswordImg} />
            </Flex>
            <Heading size="h-sm" align="center">
              {t("SESSION_PASSWORD_RESET.SENT_MESSAGE")}
            </Heading>
          </Flex>
        </Box>
      </AnimatedLayoutModal>
    );
  };

  const renderForm = (formikProps: FormikProps<IPasswordResetFormValues>) => <PasswordResetForm form={formikProps} />;

  const renderResetPassword = () => {
    return (
      <AnimatedLayoutModal size="sm">
        <Box px={["sm", null, "xxl"]}>
          <Flex flexDirection="column" flexGrow={1}>
            <Flex flexDirection="column" gap="md">
              <Flex gap="lg" flexDirection="column">
                <Formik
                  initialValues={FORM_EMPTY_INITIAL_VALUES}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}>
                  {props => renderForm(props)}
                </Formik>
              </Flex>
              <Flex flexDirection="row" justifyContent="center">
                <Paragraph size="p-xs" align="center" color="n500">
                  <Trans ns="public" i18nKey="GOOGLE_RECAPTCHA_INFORMATION">
                    <Link href="https://policies.google.com/privacy" target="_blank" />
                    <Link href="https://policies.google.com/terms" target="_blank" />
                  </Trans>
                </Paragraph>
              </Flex>
            </Flex>
          </Flex>
        </Box>
      </AnimatedLayoutModal>
    );
  };
  return (
    <>
      {!sent && renderResetPassword()}
      {sent && renderSent()}
    </>
  );
});
